import React, { useEffect, useState } from "react";
import { getAllEmployees } from "../../../../api/hrDashboard";
import { useNavigate } from "react-router-dom";
import {
  AiOutlineUserAdd,
  AiOutlineCheckCircle,
  AiOutlineDownload,
  AiOutlineClockCircle,
} from "react-icons/ai";
import { MdPayments, MdOutlineHomeWork } from "react-icons/md";
import { HiOutlineCheckBadge } from "react-icons/hi2";
import { Modal } from "react-bootstrap";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const RightPanel = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // ── Leave Modal State ──
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState("");

  // ── Payroll Modal State ──
  const [showPayrollModal, setShowPayrollModal] = useState(false);
  const [payrollSubmitted, setPayrollSubmitted] = useState(false);
  const [payrollProcessing, setPayrollProcessing] = useState(false);
  const [payrollError, setPayrollError] = useState("");

  // ── Leave Modal Handlers ──
  const handleCloseModal = () => {
    setShowLeaveModal(false);
    setSelectedAction(null);
    setRejectionReason("");
    setSubmitted(false);
    setConfirming(false);
    setConfirmError("");
  };

  const handleConfirm = async () => {
    setConfirming(true);
    setConfirmError("");
    try {
      if (selectedAction === "approve") {
        // 🔁 Replace with real API: await approveLeave(leaveId);
        await new Promise((res) => setTimeout(res, 800));
      } else {
        // 🔁 Replace with real API: await rejectLeave(leaveId, rejectionReason);
        await new Promise((res) => setTimeout(res, 800));
      }
      setSubmitted(true);
    } catch (err) {
      setConfirmError("Something went wrong. Please try again.");
    } finally {
      setConfirming(false);
    }
  };

  // ── Payroll Modal Handlers ──
  const handleClosePayrollModal = () => {
    setShowPayrollModal(false);
    setPayrollSubmitted(false);
    setPayrollProcessing(false);
    setPayrollError("");
  };

  const handleGeneratePayroll = async () => {
    setPayrollProcessing(true);
    setPayrollError("");
    try {
      // 🔁 Replace with real API: await generatePayroll();
      await new Promise((res) => setTimeout(res, 1000));
      setPayrollSubmitted(true);
    } catch (err) {
      setPayrollError("Failed to generate payroll. Please try again.");
    } finally {
      setPayrollProcessing(false);
    }
  };

  // ── Excel Export Handler ──
  const handleExportExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Employees");

    const exportDate = new Date().toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

    // ── Header: Title rows ──
    worksheet.mergeCells("A1:F1");
    worksheet.getCell("A1").value = "Employee Export Report";
    worksheet.getCell("A1").font = { bold: true, size: 14 };
    worksheet.getCell("A1").alignment = { horizontal: "center" };

    worksheet.mergeCells("A2:F2");
    worksheet.getCell("A2").value = `Exported On: ${exportDate}`;
    worksheet.getCell("A2").font = { size: 10, color: { argb: "FF6B7280" } };
    worksheet.getCell("A2").alignment = { horizontal: "center" };

    // ── Empty spacer row ──
    worksheet.addRow([]);

    // ── Summary row ──
    const active = employees.filter((e) => !e.deboardEmp);
    const deboarded = employees.filter((e) => e.deboardEmp);

    worksheet.mergeCells("A4:F4");
    worksheet.getCell("A4").value = `Total Employees: ${employees.length}   |   Active: ${active.length}   |   Deboarded: ${deboarded.length}`;
    worksheet.getCell("A4").font = { bold: true, size: 10 };
    worksheet.getCell("A4").alignment = { horizontal: "left" };

    // ── Empty spacer row ──
    worksheet.addRow([]);

    // ── Table Header ──
    const headerRow = worksheet.addRow([
      "Full Name",
      "Department",
      "Designation",
      "Date of Joining",
      "Email",
      "Status",
    ]);

    headerRow.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 11 };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF4F6EF7" }, // Brand blue matching dashboard
      };
      cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.border = {
        top: { style: "thin" },
        bottom: { style: "thin" },
        left: { style: "thin" },
        right: { style: "thin" },
      };
    });
    headerRow.height = 22;

    // ── Table Data Rows ──
    employees.forEach((e, idx) => {
      const isActive = !e.deboardEmp;
      const fullName = `${e.firstName || ""} ${e.lastName || ""}`.trim() || "—";
      const doj = e.dateOfJoining
        ? new Date(e.dateOfJoining).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
        : "—";

      const row = worksheet.addRow([
        fullName,
        e.departmentName || "—",
        e.designationName || "—",
        doj,
        e.emailId || e.email || "—",
        isActive ? "Active" : "Deboarded",
      ]);

      // Alternating row background
      const rowBg = idx % 2 === 0 ? "FFFFFFFF" : "FFF8FAFC";

      row.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: rowBg },
        };
        cell.alignment = { horizontal: "center", vertical: "middle" };
        cell.border = {
          top: { style: "thin", color: { argb: "FFE5E7EB" } },
          bottom: { style: "thin", color: { argb: "FFE5E7EB" } },
          left: { style: "thin", color: { argb: "FFE5E7EB" } },
          right: { style: "thin", color: { argb: "FFE5E7EB" } },
        };
        cell.font = { size: 10 };
      });

      // Status cell colour: green for Active, red for Deboarded
      const statusCell = row.getCell(6);
      statusCell.font = {
        bold: true,
        size: 10,
        color: { argb: isActive ? "FF16A34A" : "FFDC2626" },
      };
    });

    // ── Column Widths ──
    worksheet.getColumn(1).width = 24; // Full Name
    worksheet.getColumn(2).width = 20; // Department
    worksheet.getColumn(3).width = 22; // Designation
    worksheet.getColumn(4).width = 18; // Date of Joining
    worksheet.getColumn(5).width = 28; // Email
    worksheet.getColumn(6).width = 14; // Status

    // ── Download ──
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, `Employees_${exportDate.replace(/ /g, "_")}.xlsx`);
  };

  useEffect(() => {
    getAllEmployees()
      .then((res) => setEmployees(res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const active = employees.filter((e) => !e.deboardEmp);

  const lateCheckins = active.slice(0, 2).map((e, i) => ({
    name: `${e.firstName || "Name"} ${e.lastName || ""}`.trim(),
    time: i === 0 ? "9:45 AM" : "10:12 AM",
    late: i === 0 ? "+45 min" : "+1h 12min",
  }));

  const wfhList = active.slice(0, 3).map((e) => ({
    name: `${e.firstName || "Employee"} ${e.lastName || ""}`.trim(),
    dept: e.departmentName || "--",
  }));

  const isConfirmDisabled =
    confirming ||
    !selectedAction ||
    (selectedAction === "reject" && !rejectionReason.trim());

  return (
    <>
      <style>{`
        .rp-card {
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 10px;
          padding: 16px;
          margin-bottom: 20px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.02);
        }
        .rp-qa-title {
          font-weight: 700;
          font-size: 14px;
          color: #1e293b;
          margin-bottom: 16px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .rp-btn-container { display: flex; flex-direction: column; gap: 10px; }
        .rp-btn {
          display: flex;
          align-items: center;
          gap: 12px;
          width: 100%;
          border: none;
          border-radius: 8px;
          padding: 12px 16px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          text-align: left;
        }
        .rp-btn span { flex-grow: 1; }
        .rp-btn:hover { transform: translateY(-1px); box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1); }
        .rp-btn:active { transform: translateY(0); }
        .rp-btn-blue   { background: #2E5CB8; color: #fff; }
        .rp-btn-green  { background: #22c55e; color: #fff; }
        .rp-btn-dark   { background: #1e293b; color: #fff; }
        .rp-btn-outline {
          background: #fff;
          color: #374151;
          border: 1px solid #e5e7eb !important;
        }
        .rp-sec-title {
          font-size: 13px; font-weight: 700;
          display: flex; align-items: center; gap: 8px;
          margin-bottom: 16px;
        }
        .rp-late-row {
          display: flex; justify-content: space-between; align-items: center;
          padding: 8px 0; border-bottom: 1px solid #f8fafc;
        }
        .rp-late-row:last-child { border-bottom: none; }
        .rp-late-name { font-size: 12px; font-weight: 600; color: #1e293b; }
        .rp-late-time { font-size: 11px; color: #64748b; margin-top: 2px; }
        .rp-late-mins {
          font-size: 11px; font-weight: 700; color: #ef4444;
          background: #fef2f2; padding: 2px 8px; border-radius: 12px;
        }
        .rp-wfh-row {
          display: flex; justify-content: space-between; align-items: center;
          padding: 8px 0; font-size: 12px;
        }
        .rp-wfh-name { font-weight: 500; color: #1e293b; }
        .rp-wfh-dept {
          color: #64748b; font-size: 11px;
          background: #f1f5f9; padding: 2px 8px; border-radius: 4px;
        }
        .rp-loader { font-size: 12px; color: #9ca3af; text-align: center; padding: 10px; }

        /* ── Shared Modal Styles ── */
        .lm-modal-header {
          background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
          color: #fff;
          border-radius: 8px 8px 0 0;
          padding: 14px 18px;
          display: flex; align-items: center; justify-content: space-between;
        }
        .pm-modal-header {
          background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
          color: #fff;
          border-radius: 8px 8px 0 0;
          padding: 14px 18px;
          display: flex; align-items: center; justify-content: space-between;
        }
        .lm-modal-header-title {
          font-size: 15px; font-weight: 700;
          display: flex; align-items: center; gap: 8px; margin: 0;
        }
        .lm-close-btn {
          background: rgba(255,255,255,0.2); border: none; color: #fff;
          width: 28px; height: 28px; border-radius: 50%; font-size: 16px;
          cursor: pointer; display: flex; align-items: center;
          justify-content: center; transition: background 0.2s;
        }
        .lm-close-btn:hover { background: rgba(255,255,255,0.35); }
        .lm-close-btn:disabled { opacity: 0.5; cursor: not-allowed; }

        .lm-info-chip {
          display: inline-flex; align-items: center; gap: 5px;
          background: #f1f5f9; border-radius: 6px; padding: 5px 10px;
          font-size: 12px; color: #475569; margin-bottom: 14px;
        }

        /* ── Leave Modal ── */
        .lm-action-row { display: flex; gap: 10px; margin-bottom: 14px; }
        .lm-action-btn {
          flex: 1; padding: 10px 8px; border-radius: 8px;
          font-size: 13px; font-weight: 600;
          border: 2px solid transparent; cursor: pointer;
          transition: all 0.2s; display: flex;
          align-items: center; justify-content: center; gap: 6px;
        }
        .lm-action-btn:disabled { opacity: 0.6; cursor: not-allowed; }
        .lm-approve { background: #f0fdf4; color: #16a34a; border-color: #bbf7d0; }
        .lm-approve:hover:not(:disabled), .lm-approve.active {
          background: #22c55e; color: #fff; border-color: #22c55e;
        }
        .lm-reject { background: #fff1f2; color: #dc2626; border-color: #fecdd3; }
        .lm-reject:hover:not(:disabled), .lm-reject.active {
          background: #ef4444; color: #fff; border-color: #ef4444;
        }

        .lm-reason-label { font-size: 13px; font-weight: 600; color: #374151; margin-bottom: 6px; }
        .lm-reason-textarea {
          width: 100%; border: 1px solid #e5e7eb; border-radius: 8px;
          padding: 10px 12px; font-size: 13px; color: #1e293b;
          resize: vertical; min-height: 90px; outline: none; transition: border-color 0.2s;
        }
        .lm-reason-textarea:focus {
          border-color: #ef4444; box-shadow: 0 0 0 3px rgba(239,68,68,0.1);
        }
        .lm-reason-textarea:disabled { background: #f8fafc; opacity: 0.7; }

        .lm-error-box {
          background: #fff1f2; border: 1px solid #fecdd3; border-radius: 8px;
          padding: 8px 12px; font-size: 12px; color: #dc2626;
          margin-top: 10px; display: flex; align-items: center; gap: 6px;
        }

        .lm-success-box { text-align: center; padding: 20px 10px; }
        .lm-success-icon { font-size: 42px; margin-bottom: 10px; }
        .lm-success-title { font-size: 16px; font-weight: 700; color: #1e293b; margin-bottom: 6px; }
        .lm-success-sub { font-size: 13px; color: #6b7280; }

        /* ── Payroll Modal ── */
        .pm-info-chip {
          display: inline-flex; align-items: center; gap: 5px;
          background: #f8fafc; border-radius: 6px; padding: 5px 10px;
          font-size: 12px; color: #475569; margin-bottom: 14px;
          border: 1px solid #e2e8f0;
        }
        .pm-notice-box {
          background: #fffbeb; border: 1px solid #fde68a;
          border-radius: 8px; padding: 12px 14px;
          font-size: 13px; color: #92400e;
          display: flex; align-items: flex-start; gap: 8px;
          margin-bottom: 14px; line-height: 1.5;
        }
        .pm-notice-icon { font-size: 16px; flex-shrink: 0; margin-top: 1px; }
        .pm-generate-btn {
          width: 100%; padding: 12px; border-radius: 8px; border: none;
          background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
          color: #fff; font-weight: 700; font-size: 14px;
          cursor: pointer; transition: all 0.2s;
          display: flex; align-items: center; justify-content: center; gap: 8px;
        }
        .pm-generate-btn:hover:not(:disabled) {
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(30,41,59,0.3);
        }
        .pm-generate-btn:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
        .pm-success-box { text-align: center; padding: 20px 10px; }
        .pm-success-icon { font-size: 48px; margin-bottom: 12px; }
        .pm-success-title { font-size: 16px; font-weight: 700; color: #1e293b; margin-bottom: 6px; }
        .pm-success-sub { font-size: 13px; color: #6b7280; line-height: 1.5; }

        /* ── Spinner ── */
        .lm-spinner {
          display: inline-block; width: 14px; height: 14px;
          border: 2px solid rgba(255,255,255,0.4); border-top-color: #fff;
          border-radius: 50%; animation: lm-spin 0.6s linear infinite;
          margin-right: 6px; vertical-align: middle;
        }
        @keyframes lm-spin { to { transform: rotate(360deg); } }

        /* ── Footer ── */
        .lm-footer-row { display: flex; gap: 8px; width: 100%; }
        .lm-btn-cancel {
          flex: 1; padding: 10px; border-radius: 8px;
          border: 1px solid #e5e7eb; background: #fff; color: #374151;
          font-weight: 600; font-size: 13px; cursor: pointer; transition: background 0.2s;
        }
        .lm-btn-cancel:hover:not(:disabled) { background: #f8fafc; }
        .lm-btn-cancel:disabled { opacity: 0.6; cursor: not-allowed; }

        .lm-btn-confirm {
          flex: 1; padding: 10px; border-radius: 8px; border: none;
          font-weight: 600; font-size: 13px; cursor: pointer; transition: all 0.2s;
          display: flex; align-items: center; justify-content: center;
        }
        .lm-btn-confirm:disabled { background: #e5e7eb !important; color: #9ca3af !important; cursor: not-allowed; }
        .lm-btn-confirm.approve { background: #22c55e; color: #fff; }
        .lm-btn-confirm.approve:hover:not(:disabled) { background: #16a34a; }
        .lm-btn-confirm.reject  { background: #ef4444; color: #fff; }
        .lm-btn-confirm.reject:hover:not(:disabled)  { background: #dc2626; }
        .lm-btn-confirm.default { background: #e5e7eb; color: #9ca3af; }

        .lm-btn-close-full {
          width: 100%; padding: 10px; border-radius: 8px; border: none;
          background: #1e293b; color: #fff; font-weight: 600;
          font-size: 13px; cursor: pointer; transition: background 0.2s;
        }
        .lm-btn-close-full:hover { background: #334155; }

        /* ── Responsive ── */
        @media (max-width: 576px) {
          .lm-action-row { flex-direction: column; }
          .lm-action-btn { width: 100%; }
          .lm-footer-row { flex-direction: column; }
          .pm-generate-btn { font-size: 13px; padding: 11px; }
        }
      `}</style>

      {/* ── Quick Actions Card ── */}
      <div className="rp-card">
        <div className="rp-qa-title">Quick Actions</div>
        <div className="rp-btn-container">
          <button
            className="rp-btn rp-btn-blue"
            onClick={() => navigate("/onboarddeboard/employees/list")}
          >
            
            <AiOutlineUserAdd size={18} />
            <span>Add Employee</span>
          </button>

          <button
            className="rp-btn rp-btn-green"
            onClick={() => setShowLeaveModal(true)}
          >
            <AiOutlineCheckCircle size={18} />
            <span>Approve Leaves</span>
          </button>

          <button
            className="rp-btn rp-btn-dark"
            onClick={() => setShowPayrollModal(true)}
          >
            <MdPayments size={18} />
            <span>Generate Payroll</span>
          </button>

          {/* ✅ Replaced plain CSV with structured ExcelJS export */}
          <button
            className="rp-btn rp-btn-outline"
            onClick={handleExportExcel}
          >
            <AiOutlineDownload size={18} />
            <span>Export Data</span>
          </button>
        </div>
      </div>

      {/* ── Late Check-ins Card ── */}
      <div className="rp-card">
        <div className="rp-sec-title" style={{ color: "#ef4444" }}>
          <AiOutlineClockCircle size={18} /> Late Check-ins
        </div>
        {loading ? (
          <div className="rp-loader">Loading...</div>
        ) : lateCheckins.length === 0 ? (
          <div
            style={{
              fontSize: 12,
              color: "#22c55e",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <HiOutlineCheckBadge size={16} /> No late check-ins
          </div>
        ) : (
          lateCheckins.map((l, i) => (
            <div key={i} className="rp-late-row">
              <div>
                <div className="rp-late-name">{l.name}</div>
                <div className="rp-late-time">{l.time}</div>
              </div>
              <span className="rp-late-mins">{l.late}</span>
            </div>
          ))
        )}
      </div>

      {/* ── Working From Home Card ── */}
      <div className="rp-card">
        <div className="rp-sec-title" style={{ color: "#4f6ef7" }}>
          <MdOutlineHomeWork size={18} /> Working From Home
        </div>
        {loading ? (
          <div className="rp-loader">Loading...</div>
        ) : wfhList.length === 0 ? (
          <div style={{ fontSize: 12, color: "#9ca3af" }}>
            No employees found
          </div>
        ) : (
          wfhList.map((w, i) => (
            <div key={i} className="rp-wfh-row">
              <span className="rp-wfh-name">{w.name}</span>
              <span className="rp-wfh-dept">{w.dept}</span>
            </div>
          ))
        )}
      </div>

      {/* ════════════════════════════════════
          LEAVE APPROVAL MODAL
      ════════════════════════════════════ */}
      <Modal
        show={showLeaveModal}
        onHide={handleCloseModal}
        centered
        size="sm"
        contentClassName="border-0"
      >
        <div className="lm-modal-header">
          <p className="lm-modal-header-title">
            <AiOutlineCheckCircle size={18} />
            Leave Approval
          </p>
          <button
            className="lm-close-btn"
            onClick={handleCloseModal}
            disabled={confirming}
          >
            ✕
          </button>
        </div>

        <Modal.Body style={{ padding: "18px" }}>
          {submitted ? (
            <div className="lm-success-box">
              <div className="lm-success-icon">
                {selectedAction === "approve" ? "✅" : "❌"}
              </div>
              <div className="lm-success-title">
                {selectedAction === "approve"
                  ? "Leave Approved!"
                  : "Leave Rejected!"}
              </div>
              <div className="lm-success-sub">
                {selectedAction === "approve"
                  ? "The employee has been notified."
                  : "The employee has been notified with your reason."}
              </div>
            </div>
          ) : (
            <>
              <div className="lm-info-chip">
                📋 &nbsp;Pending leave request action
              </div>
              <p
                style={{
                  fontSize: "13px",
                  color: "#475569",
                  marginBottom: "14px",
                }}
              >
                Choose whether to approve or reject this leave request.
              </p>
              <div className="lm-action-row">
                <button
                  className={`lm-action-btn lm-approve ${
                    selectedAction === "approve" ? "active" : ""
                  }`}
                  onClick={() => {
                    setSelectedAction("approve");
                    setConfirmError("");
                  }}
                  disabled={confirming}
                >
                  ✓ Approve
                </button>
                <button
                  className={`lm-action-btn lm-reject ${
                    selectedAction === "reject" ? "active" : ""
                  }`}
                  onClick={() => {
                    setSelectedAction("reject");
                    setConfirmError("");
                  }}
                  disabled={confirming}
                >
                  ✕ Reject
                </button>
              </div>
              {selectedAction === "reject" && (
                <div>
                  <div className="lm-reason-label">
                    Reason for Rejection{" "}
                    <span style={{ color: "#ef4444" }}>*</span>
                  </div>
                  <textarea
                    className="lm-reason-textarea"
                    placeholder="Please provide a reason for rejecting this leave request..."
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    disabled={confirming}
                  />
                </div>
              )}
              {confirmError && (
                <div className="lm-error-box">⚠️ {confirmError}</div>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer
          style={{ padding: "12px 18px", borderTop: "1px solid #f1f5f9" }}
        >
          {submitted ? (
            <button className="lm-btn-close-full" onClick={handleCloseModal}>
              Close
            </button>
          ) : (
            <div className="lm-footer-row">
              <button
                className="lm-btn-cancel"
                onClick={handleCloseModal}
                disabled={confirming}
              >
                Cancel
              </button>
              <button
                className={`lm-btn-confirm ${
                  selectedAction === "approve"
                    ? "approve"
                    : selectedAction === "reject"
                    ? "reject"
                    : "default"
                }`}
                disabled={isConfirmDisabled}
                onClick={handleConfirm}
              >
                {confirming && <span className="lm-spinner" />}
                {confirming
                  ? "Processing..."
                  : selectedAction === "approve"
                  ? "✓ Confirm Approval"
                  : selectedAction === "reject"
                  ? "✕ Confirm Rejection"
                  : "Confirm"}
              </button>
            </div>
          )}
        </Modal.Footer>
      </Modal>

      {/* ════════════════════════════════════
          GENERATE PAYROLL MODAL
      ════════════════════════════════════ */}
      <Modal
        show={showPayrollModal}
        onHide={handleClosePayrollModal}
        centered
        size="sm"
        contentClassName="border-0"
      >
        <div className="pm-modal-header">
          <p className="lm-modal-header-title">
            <MdPayments size={18} />
            Generate Payroll
          </p>
          <button
            className="lm-close-btn"
            onClick={handleClosePayrollModal}
            disabled={payrollProcessing}
          >
            ✕
          </button>
        </div>

        <Modal.Body style={{ padding: "18px" }}>
          {payrollSubmitted ? (
            <div className="pm-success-box">
              <div className="pm-success-icon">✅</div>
              <div className="pm-success-title">Payroll Generated!</div>
              <div className="pm-success-sub">
                Payroll has been successfully generated and employees will be
                notified shortly.
              </div>
            </div>
          ) : (
            <>
              <div className="pm-info-chip">
                💰 &nbsp;Payroll generation action
              </div>

              <p
                style={{
                  fontSize: "13px",
                  color: "#475569",
                  marginBottom: "14px",
                  lineHeight: "1.5",
                }}
              >
                You are about to generate payroll for all active employees.
                Please confirm to proceed.
              </p>

              <div className="pm-notice-box">
                <span className="pm-notice-icon">⚠️</span>
                <span>
                  This action will process payments for all active employees.
                  Make sure attendance and leave records are up to date before
                  generating.
                </span>
              </div>

              {payrollError && (
                <div className="lm-error-box">⚠️ {payrollError}</div>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer
          style={{ padding: "12px 18px", borderTop: "1px solid #f1f5f9" }}
        >
          {payrollSubmitted ? (
            <button
              className="lm-btn-close-full"
              onClick={handleClosePayrollModal}
            >
              Close
            </button>
          ) : (
            <div className="lm-footer-row">
              <button
                className="lm-btn-cancel"
                onClick={handleClosePayrollModal}
                disabled={payrollProcessing}
              >
                Cancel
              </button>
              <button
                className="pm-generate-btn"
                style={{ flex: 1 }}
                onClick={handleGeneratePayroll}
                disabled={payrollProcessing}
              >
                {payrollProcessing && <span className="lm-spinner" />}
                {payrollProcessing ? "Processing..." : " Generate Now"}
              </button>
            </div>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default RightPanel;